import React from 'react';
import { Modal } from 'reactstrap';
import PropTypes from 'prop-types';

/**
 * Modal component for handling subscription/trial cancellation
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Controls modal visibility
 * @param {Function} props.onCancel - Callback for cancel action
 * @param {Function} props.onConfirm - Callback for confirm action
 * @param {boolean} props.loading - Loading state indicator
 * @param {boolean} props.isTrialCompleted - Whether trial period has ended
 */
const CancelSubscription = ({ 
  isOpen, 
  onCancel, 
  onConfirm, 
  loading = false, 
  subscriptionStatus 
}) => {
  const modalStyles = {
    modal: {
      backgroundColor: '#ffffff',
      padding: '32px',
      borderRadius: '12px',
      width: '100%',
      maxWidth: '480px',
      margin: 'auto',
      textAlign: 'center',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
      border: 'none',
    },
    header: {
      fontSize: '24px',
      fontWeight: '600',
      color: '#2c3e50',
      marginBottom: '16px',
      lineHeight: '1.4',
    },
    body: {
      fontSize: '16px',
      color: '#5a6c7d',
      marginBottom: '32px',
      lineHeight: '1.6',
    },
    notice: {
      fontWeight: '500',
      color: '#e74c3c',
      marginTop: '12px',
    },
    buttonContainer: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'center',
      flexWrap: 'wrap',
    },
    button: {
      padding: '12px 24px',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: '500',
      minWidth: '140px',
      transition: 'all 0.2s ease',
      outline: 'none',
      '&:focus': {
        boxShadow: '0 0 0 3px rgba(0, 123, 255, 0.25)',
      },
    },
    cancelButton: {
      backgroundColor: '#f8f9fa',
      color: '#495057',
      border: '2px solid #dee2e6',
      '&:hover': {
        backgroundColor: '#e9ecef',
        borderColor: '#adb5bd',
      },
      '&:disabled': {
        opacity: 0.6,
        cursor: 'not-allowed',
      },
    },
    confirmButton: {
      backgroundColor: '#dc3545',
      color: '#ffffff',
      '&:hover': {
        backgroundColor: '#c82333',
      },
      '&:disabled': {
        opacity: 0.6,
        cursor: 'not-allowed',
        backgroundColor: '#dc3545',
      },
    },
  };

  const getModalContent = () => {
    if (subscriptionStatus === "Subscription") {
      return {
        title: 'Cancel Subscription',
        message: (
          <>
            If you cancel your subscription now, you will retain access until the end of your current billing period.
            <div style={modalStyles.notice}>
              Please note: Payments already processed are non-refundable.
            </div>
          </>
        ),
        cancelText: 'Keep Subscription',
        confirmText: 'Cancel Subscription',
      };
    }

    return {
      title: 'Cancel Trial',
      message: (
        <>
          Your trial period is currently active. Canceling now will prevent any future charges.
          <div style={modalStyles.notice}>
            Once the trial period ends, payment will be automatically processed and is non-refundable.
          </div>
        </>
      ),
      cancelText: 'Keep Trial',
      confirmText: 'Cancel Trial',
    };
  };

  const content = getModalContent();

  const handleCancel = () => {
    if (!loading && onCancel) {
      onCancel();
    }
  };

  const handleConfirm = () => {
    if (!loading && onConfirm) {
      onConfirm();
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      toggle={handleCancel} 
      centered 
      backdrop="static"
      keyboard={!loading}
    >
      <div style={modalStyles.modal}>
        <div style={modalStyles.header}>
          {content.title}
        </div>
        
        <div style={modalStyles.body}>
          {content.message}
        </div>
        
        <div style={modalStyles.buttonContainer}>
          <button
            type="button"
            style={{
              ...modalStyles.button,
              ...modalStyles.cancelButton,
            }}
            onClick={handleCancel}
            disabled={loading}
            aria-label={content.cancelText}
          >
            {content.cancelText}
          </button>
          
          <button
            type="button"
            style={{
              ...modalStyles.button,
              ...modalStyles.confirmButton,
            }}
            onClick={handleConfirm}
            disabled={loading}
            aria-label={loading ? 'Processing request' : content.confirmText}
          >
            {loading ? 'Processing...' : content.confirmText}
          </button>
        </div>
      </div>
    </Modal>
  );
};

CancelSubscription.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  isTrialCompleted: PropTypes.bool,
};

CancelSubscription.defaultProps = {
  loading: false,
  isTrialCompleted: false,
};

export default CancelSubscription;